import { z } from "zod";
import { pageQuerySchema } from "./common.ts";

export const cartItemSchema = z.object({
  skuId: z.number().int().positive(),
  quantity: z.number().int().min(1).max(9999),
});
export type CartItemInput = z.infer<typeof cartItemSchema>;

export const setQuantitySchema = z.object({
  quantity: z.number().int().min(0).max(9999),
});

/** 结算预览 / 购物车视图。⚠️ 按供应商分组，因为下单时按供应商拆单。 */
export const checkoutGroupSchema = z.object({
  supplierOrgId: z.number().int(),
  supplierName: z.string(),
  lines: z.array(
    z.object({
      skuId: z.number().int(),
      title: z.string(),
      unitPrice: z.number().int(),
      quantity: z.number().int(),
      subtotal: z.number().int(),
      stock: z.number().int(),
      available: z.boolean(),
      /**
       * 商品图。⚠️ 服务端已经把对象存储 key 转成可访问地址了，
       *    前端直接用，别自己拼 —— 换存储或换域名时要改的地方会散到各页面去。
       *    SKU 没单独配图时是商品主图（大部分 SKU 不会单独配图）。
       */
      imageUrl: z.string().default(""),
      /**
       * 这一行的吊牌价小计（指导零售价 × 数量）。
       * 🔴 用来在结算页显示「商品原价 ¥7280 / 店铺优惠 −¥1456 / 实付 ¥5824」——
       *    不列出来的话用户不知道自己因为挂在这个商家名下省了多少，
       *    而那正是他不去别处买的理由。
       * ⚠️ 没设吊牌价的商品等于实付小计，前端据此不显示优惠行。
       */
      originalSubtotal: z.number().int(),
    }),
  ),
  /** 吊牌价合计（商品原价）。⚠️ 不含运费。 */
  originalAmount: z.number().int(),
  /**
   * 店铺优惠 = 吊牌价合计 − 实付商品额。
   * ⚠️ 跟券的 `discountAmount` 是**两回事**：这个是"这家店本来就卖得比
   *    平台指导价便宜"，券是在此之上再减。两个都要单独列，
   *    合成一个数的话用户搞不清哪部分是店铺给的、哪部分是券给的。
   */
  shopDiscount: z.number().int(),
  goodsAmount: z.number().int(),
  freightAmount: z.number().int(),
  discountAmount: z.number().int(),
  payableAmount: z.number().int(),
});

export const checkoutPreviewSchema = z.object({
  groups: z.array(checkoutGroupSchema),
  issues: z.array(z.object({ skuId: z.number().int(), title: z.string(), reason: z.string() })),
  /** 试算通过的券。null = 没输券码，或者输了但用不了（原因看 couponError）。 */
  coupon: z
    .object({
      code: z.string(),
      name: z.string(),
      discount: z.number().int(),
      /** 🔴 券作用在哪一家供应商那一单上 —— 拆单时必须告诉用户，否则他会以为整单都减了。 */
      supplierOrgId: z.number().int(),
    })
    .nullable(),
  couponError: z.string().nullable(),
  totalGoods: z.number().int(),
  totalFreight: z.number().int(),
  totalDiscount: z.number().int(),
  totalPayable: z.number().int(),
  /** 全部分组加起来的吊牌价合计。 */
  totalOriginal: z.number().int(),
  /** 全部分组加起来的店铺优惠。 */
  totalShopDiscount: z.number().int(),
});

export type CheckoutGroup = z.infer<typeof checkoutGroupSchema>;
export type CheckoutPreview = z.infer<typeof checkoutPreviewSchema>;

export const createOrderSchema = z.object({
  items: z.array(cartItemSchema).min(1).max(200),
  addressId: z.number().int().positive(),
  buyerNote: z.string().max(200).optional(),
  /** 幂等键，客户端生成。重复提交不产生第二组单。 */
  idempotencyKey: z.string().min(8).max(64),
  /** 优惠券码。⚠️ 拆单时只作用于商品额最大的那一张。 */
  couponCode: z.string().min(1).max(32).optional(),
  /**
   * 🔴 用户在结算页看到的应付合计（分）。服务端会比对，对不上直接拒。
   *    价格在这个平台里变动频繁（分销商改加价率、等级升级、上游调价）——
   *    不带这个的话，用户看着一个数、被扣另一个数。
   */
  expectedTotal: z.number().int().min(0).optional(),
  /**
   * 从哪家店下的单。
   *
   * 🔴 **只对「还没有归属的买家」起作用** —— 他从谁的店下单就归属谁，
   *    这是店铺链接取代邀请码成为裂变入口的关键一环。
   *
   * ⚠️ 已有归属的买家传了也不会被抢走（服务端走「先绑定者优先」）。
   *    否则只要把链接发进对方的客户群，就能把人家辛苦拉来的客户变成自己的。
   */
  shopId: z.number().int().positive().optional(),
});
export type CreateOrderInput = z.infer<typeof createOrderSchema>;

export const orderListQuerySchema = pageQuerySchema.extend({
  payment: z
    .enum(["pending", "paid", "partial_refunded", "refunded", "failed", "expired"])
    .optional(),
  fulfillment: z
    .enum(["pending", "shipped", "delivered", "completed", "cancelled", "after_sale"])
    .optional(),
});

/**
 * 确认收到线下货款。
 *
 * 🔴 **不收金额** —— 收多少是订单上算好的（`payableAmount`），
 *    让前端传等于点一下就能把「已收 ¥5824」记成「已收 ¥1」。
 *    确认的是"这笔钱到账了"，不是"到了多少"。
 */
export const confirmOfflinePaymentSchema = z.object({
  /** 走的什么渠道：微信 / 支付宝 / 银行转账 / 现金 / 对公。留痕用。 */
  method: z.string().min(1).max(30).optional(),
  /** 备注（流水号、经手人之类）。线下收款没有第三方流水，这是唯一凭据。 */
  note: z.string().max(500).optional(),
});

/**
 * 改店铺信息。
 *
 * 🔴 **联系方式在线下收款模式下是必需品** —— 买家要直接联系卖家付款。
 * ⚠️ 全 optional：只改电话时不该把店名一起清空。
 */
export const updateShopSchema = z.object({
  displayName: z.string().max(60).optional(),
  contactName: z.string().max(30).optional(),
  contactPhone: z.string().max(20).optional(),
});

/**
 * ═══════════════════════════════════════════════════════════════
 *  店铺自己的货架
 * ═══════════════════════════════════════════════════════════════
 */

/** 店铺分类。⚠️ 跟平台的全站品类是两回事，见 `shop_categories` 上的说明。 */
export const shopCategorySchema = z.object({
  name: z.string().min(1).max(20),
  /** 越小越靠前。 */
  sortOrder: z.number().int().min(0).max(999).optional().default(0),
  /**
   * 绑定平台品类 → **自动货架**：店里属于这个品类的商品自动都在里面，
   * 上游明天新上一件也会自己出现，不用他再去归一次类。
   * 不填就是自定义货架（「主推」「样板间同款」），要一件件放。
   */
  platformCategoryId: z.number().int().positive().nullable().optional(),
});

export const reorderSchema = z.object({
  ids: z.array(z.number().int().positive()).max(200),
});

/** ⚠️ `null` = 移出分类，不是"没传" —— 所以是 nullable 不是 optional。 */
export const setProductCategorySchema = z.object({
  categoryId: z.number().int().positive().nullable(),
});

export const shipOrderSchema = z.object({
  carrier: z.string().min(1).max(30),
  trackingNo: z.string().min(1).max(50),
});

/**
 * 收银台：给一次结算创建收款单。
 *
 * 🔴 **只收订单 id，不收金额。** 金额一律服务端从库里的 payableAmount 加出来 ——
 *    让客户端传钱等于前端改个数字就能 1 分钱买走一屋子瓷砖。
 */
export const createPaymentSchema = z.object({
  orderIds: z.array(z.number().int().positive()).min(1).max(20),
});

/**
 * 申请退款。
 * ⚠️ 不传 amount = 退剩余可退的全部（整单退）。
 */
export const requestRefundSchema = z.object({
  amount: z.number().int().positive().optional(),
  reason: z.string().min(1, "请填写退款原因").max(500),
});

export const rejectRefundSchema = z.object({
  reason: z.string().min(1, "请填写驳回理由").max(500),
});

/** 结算预览时试算券。 */
export const previewQuerySchema = z.object({
  items: z.array(cartItemSchema).min(1).max(200),
  couponCode: z.string().min(1).max(32).optional(),
});

/**
 * 建券。
 *
 * 🔴 `issuerOrgId` 不在这里传 —— 服务端按调用者的身份定死，
 *    否则商家可以把成本记到别人头上。
 */
export const createCouponSchema = z.object({
  code: z.string().min(4).max(32).regex(/^[A-Za-z0-9-]+$/, "券码只能用字母数字和连字符"),
  name: z.string().min(1).max(50),
  kind: z.enum(["fixed", "percent"]),
  /** fixed 时是分，percent 时是 1—99（95 表示打 95 折）。 */
  value: z.number().int().positive(),
  threshold: z.number().int().min(0).optional(),
  /** 0 = 不限量 */
  totalCount: z.number().int().min(0).optional(),
  perUserLimit: z.number().int().min(1).max(99).optional(),
  startAt: z.iso.datetime().optional(),
  endAt: z.iso.datetime().optional(),
});
